import type {
  CornerEnumKey,
  CustomColorSet,
  LayoutBlock,
  PageMeta,
  SiteMode,
  SpacingEnumKey,
  TypographyEnumKey,
} from '@web-studio/shared-schemas';
import {
  LAYOUT_COMPONENT_TYPE,
  LEGAL_BODY_HTML_FIELD_KEY,
  LEGAL_IMPRINT_FIELD_KEYS,
  LEGAL_PRIVACY_FIELD_KEYS,
  presentLegalBodyHtml,
  visibleLegalDefinitionKeys,
  SITE_MODE,
  SPACING_ENUM_KEYS,
  REGISTRY_BLOCK,
  buildHomepageAnchorHref,
  buildLocaleSwitcherHref,
  buildProjectThemeInlineStyle,
  compositionSiteDataAttributes,
  getPlatformLocaleDefinition,
  resolveFooterLegalLinks,
  resolveMenuGridColumns,
  resolveNavigationTree,
  resolveNavCurrentPageSlug,
  navNodeIsCurrentPage,
  NAV_PRESENTATION,
  navPresentationUsesCinematicLogo,
  readNavPresentationBlockOverride,
  resolveNavArrangementFromRegistry,
  resolveNavGround,
  resolveNavLogoSurface,
  resolveNavPresentation,
  resolveOverlayAdaptivePanelLogoPath,
  resolvePrimaryHeroRegistryKey,
  retargetPlatformNavLogoPathForSurface,
  type ResolvedNavigationNode,
} from '@web-studio/shared-schemas';
import { defaultAllergyFields, defaultCateringFields, defaultPrivatePartyFields, defaultReservationFields } from '../lib/defaultFormFields';
import {
  buildSiteImageProxyUrl,
  buildSiteImageSrcSet,
  canonicalizeMediaUrl,
} from '../lib/imageProxy';

import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { pickBlockField, pickLocalized } from '../lib/localized';
import { splitTastingCourseLabel } from '../lib/tastingCourseLabel';
import { NAV_DRAWER_HASH_SCRIPT } from '../lib/navDrawerHash';
import { parseFloorTables, renderFloorTableSelect } from '../lib/parseFloorTables';
import { parseFormFields } from '../lib/parseFormFields';
import { parseLabeledLines, parseRouteStops } from '../lib/parseLabeledLines';
import { parseMenuColumns, type MenuItem } from '../lib/parseMenuColumns';
import { resolveBlockCtaHref, resolveSectionHref, readBlockCtaIntent } from '../lib/resolveSectionHref';
import { parseHoursSchedule, type HoursScheduleRow } from '../lib/splitHoursLines';
import { resolveLegalPageHeading } from '../lib/legalPageTitle';
import {
  legalFieldLabelContentKey,
  pickChrome,
  pickImprintFieldLabel,
  pickPrivacyFieldLabel,
} from '../lib/siteChromeCopy';
import {
  LUCIDE_SSR_CLOCK,
  LUCIDE_SSR_MAP_PIN,
  resolveSectionSsrIcon,
} from '../icons/lucideSsrIconNodes';
import { safeMapEmbedUrl } from '../lib/safeGuestLinks';
import { renderLucideIconHtml } from '../icons/renderIconHtml';
import { escapeHtml } from './escape';
import { renderImageGallery } from './galleryHtml';
import { CONTACT_SSR_REGISTRY_OVERRIDES, renderContactVisitHtml } from './contactHtml';
import { renderFaqBlockHtml } from './faqHtml';
import { renderAnnouncementBarHtml } from './announcementBarHtml';
import { renderBackToTopFloatHtml, renderContactChannelsFloatHtml } from './floatChromeHtml';
import { renderUtilityBarHtml } from './utilityBarHtml';
import { FOOTER_SSR_REGISTRY_OVERRIDES, renderFooterStandardHtml } from './footerHtmlRenderers';
import { renderFormInput, renderLeadFormOpenTag, resolveLeadFormKey } from './leadFormHtml';
import { HERO_SSR_REGISTRY_OVERRIDES } from './heroHtmlRenderers';
import { MENU_SSR_REGISTRY_OVERRIDES } from './menuHtmlRenderers';
import { renderSiteSocialLinksHtml } from './siteSocialHtml';
import {
  renderReservationCallBook,
  renderReservationMessengerBook,
  renderReservationThirdPartyBook,
} from './reservationHtmlRenderers';
import {
  renderMenuItemCodedRowHtml,
  renderMenuItemDietaryTagsHtml,
  renderMenuItemNameHtml,
  renderMenuLegalLegendHtml,
} from './menuItemMarksHtml';
import { MENU_FLOW_CHIP_SCROLL_THRESHOLD } from '../blocks/menu/fullMenuDietary';
import { isDrinkLikeMenuCategoryTitle } from '../lib/menuCategoryKind';

export interface BlockHtmlRenderContext {
  locale: string;
  headingLevel: 'h1' | 'h2';
  pages?: PageMeta[];
  defaultLocale: string;
  supportedLocales?: string[];
  siteMode: SiteMode;
  vertical?: string;
  themeId?: string;
  colorsetId?: string;
  allBlocks?: LayoutBlock[];
  /** Path-based preview prefix (`/api/serve-staging/{id}`). */
  publicPathPrefix?: string | null;
  /** Current request path (may include locale prefix). */
  pathname?: string | null;
}

type BlockHtmlRenderer = (block: LayoutBlock, ctx: BlockHtmlRenderContext) => string;

/**
 * Override map: when a registryBlockKey has its own SSR template, register it
 * here. Entries override the canonical componentType fallback in renderBlockHtml.
 *
 * Add new SSR variants by writing the renderer function and mapping the key:
 *   [REGISTRY_BLOCK.presentation.heroCoffeeSpotlight]: renderHeroCoffeeSpotlight,
 */
const SSR_REGISTRY_KEY_OVERRIDES: Partial<Record<string, BlockHtmlRenderer>> = {
  ...HERO_SSR_REGISTRY_OVERRIDES,
  ...MENU_SSR_REGISTRY_OVERRIDES,
  ...FOOTER_SSR_REGISTRY_OVERRIDES,
  ...CONTACT_SSR_REGISTRY_OVERRIDES,
  [REGISTRY_BLOCK.presentation.privatePartyInquirySalon]: (block, ctx) =>
    renderPrivatePartyInquirySalon(block, ctx.locale),
  [REGISTRY_BLOCK.presentation.tastingMenuPairingCards]: (block, ctx) =>
    renderTastingMenuPairingCards(block, ctx.locale),
};

/**
 * Resolve an SSR renderer for a registry block key override, if one exists.
 */
export function resolveBlockHtmlRenderer(
  registryBlockKey?: string,
): BlockHtmlRenderer | undefined {
  if (registryBlockKey && SSR_REGISTRY_KEY_OVERRIDES[registryBlockKey]) {
    return SSR_REGISTRY_KEY_OVERRIDES[registryBlockKey];
  }
  return undefined;
}

export interface RenderSiteHtmlOptions {
  blocks: LayoutBlock[];
  locale: string;
  themeId: string;
  colorsetId?: string;
  customColorSets?: CustomColorSet[];
  spacingEnum?: SpacingEnumKey;
  cornerEnum?: CornerEnumKey;
  typographyEnum?: TypographyEnumKey;
  title?: string;
  description?: string;
  headExtras?: string;
  bodyAttributes?: string;
  pages?: PageMeta[];
  defaultLocale?: string;
  supportedLocales?: string[];
  siteMode?: SiteMode;
  vertical?: string;
  /** Path-based preview prefix (`/api/serve-staging/{id}`). */
  publicPathPrefix?: string | null;
  /** Current request path (may include locale prefix) for path-preserving switcher. */
  pathname?: string | null;
}

function isHeroBlock(block: LayoutBlock): boolean {
  if (
    block.componentType === 'Hero_Editorial_Split' ||
    block.componentType === 'Hero_Video_Atmosphere'
  ) {
    return true;
  }
  return block.registryBlockKey?.startsWith('Hero_') ?? false;
}

function resolvePrimaryHeroId(blocks: LayoutBlock[]): string | null {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  const hero = ordered.find((b) => isHeroBlock(b));
  return hero?.blockId ?? null;
}

function renderNavNodeHtml(
  node: ResolvedNavigationNode,
  blockId: string,
  locale: string,
  currentPageSlug: string | null,
  depth = 0,
  forceMega = false,
): string {
  const hasChildren = node.children.length > 0;
  const isMega = hasChildren && (forceMega || node.layout === 'mega');
  const currentAttr = navNodeIsCurrentPage(node, currentPageSlug) ? ' aria-current="page"' : '';
  const link = `<a class="ws-nav__link" href="${escapeHtml(node.href)}"${currentAttr}>${escapeHtml(node.label)}</a>`;

  if (!hasChildren) {
    return `<li class="ws-nav__item" data-nav-depth="${depth}">${link}</li>`;
  }

  const subToggleId = `ws-nav-sub-${escapeHtml(blockId)}-${escapeHtml(node.id)}`;
  const childHtml = node.children
    .map((child) => renderNavNodeHtml(child, blockId, locale, currentPageSlug, depth + 1, forceMega))
    .join('');
  const itemClass = [
    'ws-nav__item',
    'ws-nav__item--has-children',
    isMega ? 'ws-nav__item--mega' : 'ws-nav__item--dropdown',
  ].join(' ');
  const submenuClass = ['ws-nav__submenu', isMega ? 'ws-nav__submenu--mega' : '']
    .filter(Boolean)
    .join(' ');
  const submenuLabel = escapeHtml(pickChrome('navSubmenu', locale));

  return `<li class="${itemClass}" data-nav-depth="${depth}">
    <input type="checkbox" id="${subToggleId}" class="ws-nav__sub-toggle" aria-hidden="true" tabindex="-1" />
    <div class="ws-nav__item-row">${link}<label for="${subToggleId}" class="ws-nav__sub-expand" aria-label="${submenuLabel}"><span class="ws-nav__sub-expand-icon" aria-hidden="true"></span></label></div>
    <ul class="${submenuClass}">${childHtml}</ul>
  </li>`;
}

function renderNavLocaleSwitcherHtml(input: {
  locale: string;
  defaultLocale: string;
  supportedLocales: string[];
  publicPathPrefix?: string | null;
  pathname?: string | null;
  slot: 'bar' | 'drawer';
}): string {
  if (input.supportedLocales.length < 2) {
    return '';
  }
  const currentPath = input.pathname?.trim() || '/';
  const aria = escapeHtml(pickChrome('navLocale', input.locale));
  const pairMode = input.supportedLocales.length === 2;

  const localeAnchor = (code: string) => {
    const active = code === input.locale;
    const def = getPlatformLocaleDefinition(code);
    const href = escapeHtml(
      buildLocaleSwitcherHref({
        targetLocale: code,
        defaultLocale: input.defaultLocale,
        supportedLocales: input.supportedLocales,
        pathname: currentPath,
        publicPathPrefix: input.publicPathPrefix,
      }),
    );
    const codeLabel = escapeHtml(def?.pillLabel ?? code.toUpperCase());
    const nameHtml = pairMode
      ? ''
      : `<span class="ws-nav__locale-name">${escapeHtml(def?.nativeLabel ?? code)}</span>`;
    return `<a class="ws-nav__locale${active ? ' ws-nav__locale--active' : ''}" href="${href}"${active ? ' aria-current="true"' : ''} lang="${escapeHtml(code)}"><span class="ws-nav__locale-code">${codeLabel}</span>${nameHtml}</a>`;
  };

  if (pairMode) {
    const pair = input.supportedLocales
      .map((code, index) => {
        const sep =
          index > 0 ? '<span class="ws-nav__locale-sep" aria-hidden="true">|</span>' : '';
        return `<span class="ws-nav__locale-pair-item">${sep}${localeAnchor(code)}</span>`;
      })
      .join('');
    return `<nav class="ws-nav__locale-pair ws-nav__locales--${input.slot}" aria-label="${aria}">${pair}</nav>`;
  }

  const activeDef = getPlatformLocaleDefinition(input.locale);
  const triggerLabel = escapeHtml(activeDef?.pillLabel ?? input.locale.toUpperCase());
  const options = input.supportedLocales
    .map((code) => `<li class="ws-nav__locale-item">${localeAnchor(code)}</li>`)
    .join('');

  return `<details class="ws-nav__locale-switch ws-nav__locales--${input.slot}"><summary class="ws-nav__locale-trigger" aria-label="${aria}"><span class="ws-nav__locale-code">${triggerLabel}</span><span class="ws-nav__locale-chevron" aria-hidden="true"></span></summary><ul class="ws-nav__locale-menu" role="list">${options}</ul></details>`;
}

function renderNavigation(
  block: LayoutBlock,
  locale: string,
  pages: PageMeta[] | undefined,
  defaultLocale: string,
  siteMode: SiteMode,
  vertical?: string,
  themeId?: string,
  colorsetId?: string,
  allBlocks?: LayoutBlock[],
  publicPathPrefix?: string | null,
  supportedLocales?: string[],
  pathname?: string | null,
): string {
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const brandTitle = siteTitle.trim();
  const cta = pickBlockField(block.content, 'ctaLabel', locale);
  // Path-only for uploads / platform defaults — never bake cdn.* into visitor HTML.
  const logoImageRaw = block.media?.logoImage;
  // Site-wide Look from the nav block config — do not re-pair from the page hero.
  const navPresentation = resolveNavPresentation(
    themeId,
    readNavPresentationBlockOverride(block.content),
    null,
  );
  const logoSurface = resolveNavLogoSurface(themeId, navPresentation, colorsetId);
  const logoCanonical = logoImageRaw ? canonicalizeMediaUrl(logoImageRaw) : undefined;
  const logoImage = logoCanonical
    ? retargetPlatformNavLogoPathForSurface(logoCanonical, logoSurface)
    : undefined;
  const logoImagePanel =
    navPresentationUsesCinematicLogo(navPresentation) && logoCanonical
      ? resolveOverlayAdaptivePanelLogoPath(logoCanonical, themeId, colorsetId)
      : null;
  const navItems = resolveNavigationTree(block, {
    pages,
    locale,
    defaultLocale,
    siteMode,
    vertical,
    themeId,
    publicPathPrefix,
    siteBlocks: allBlocks,
  });
  const navArrangement = resolveNavArrangementFromRegistry(block.registryBlockKey);
  const currentPageSlug = resolveNavCurrentPageSlug({
    pathname,
    publicPathPrefix,
    supportedLocales,
  });
  const links = navItems
    .map((item) =>
      renderNavNodeHtml(item, block.blockId, locale, currentPageSlug, 0, false),
    )
    .join('');

  const logoHeroClass = logoImagePanel ? 'ws-nav__logo ws-nav__logo--hero' : 'ws-nav__logo';
  const logoHtml = logoImage
    ? `<img class="${logoHeroClass}" src="${escapeHtml(logoImage)}" alt="${escapeHtml(brandTitle)}" width="120" height="40" loading="eager" decoding="async" />`
    : '';
  const logoPanelHtml = logoImagePanel
    ? `<img class="ws-nav__logo ws-nav__logo--panel" src="${escapeHtml(logoImagePanel)}" alt="${escapeHtml(brandTitle)}" width="120" height="40" loading="eager" decoding="async" />`
    : '';
  const brandTextHtml = brandTitle
    ? `<span class="ws-nav__brand-text">${escapeHtml(brandTitle)}</span>`
    : '';
  const brandInner = `${logoHtml}${logoPanelHtml}${brandTextHtml}`;

  const toggleId = `ws-nav-toggle-${escapeHtml(block.blockId)}`;
  const hasLinks = navItems.length > 0;
  const arrangementClass =
    navArrangement === 'compass'
      ? 'ws-nav-compass'
      : navArrangement === 'curtain'
        ? 'ws-nav-curtain'
        : navArrangement === 'ledger'
          ? 'ws-nav-ledger'
          : navArrangement === 'monogram'
            ? 'ws-nav-monogram'
            : navArrangement === 'signal'
              ? 'ws-nav-signal'
              : 'ws-nav-rail';
  const localeOptions =
    supportedLocales && supportedLocales.length > 0 ? supportedLocales : [defaultLocale];
  const localeSwitcherBarHtml = renderNavLocaleSwitcherHtml({
    locale,
    defaultLocale,
    supportedLocales: localeOptions,
    publicPathPrefix,
    pathname,
    slot: 'bar',
  });
  const localeSwitcherDrawerHtml = renderNavLocaleSwitcherHtml({
    locale,
    defaultLocale,
    supportedLocales: localeOptions,
    publicPathPrefix,
    pathname,
    slot: 'drawer',
  });
  const ctaIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaHref',
    fallback: 'book',
  });
  const ctaHref = resolveBlockCtaHref(ctaIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaHref',
    siteMode,
    pages,
    allBlocks,
    publicPathPrefix,
  });
  const brandHref = escapeHtml(
    resolveSectionHref('top', {
      locale,
      defaultLocale,
      publicPathPrefix,
    }),
  );
  const ctaHrefEsc = escapeHtml(ctaHref);
  const ctaHtml = cta
    ? `<a class="ws-nav__cta" href="${ctaHrefEsc}">${escapeHtml(cta)}</a>`
    : '';
  const barCtaHtml = cta
    ? `<a class="ws-nav__cta ws-nav__cta--bar" href="${ctaHrefEsc}">${escapeHtml(cta)}</a>`
    : '';
  const drawerCtaHtml = cta
    ? `<a class="ws-nav__cta ws-nav__cta--drawer" href="${ctaHrefEsc}">${escapeHtml(cta)}</a>`
    : '';
  const desktopCtaHtml = cta
    ? `<a class="ws-nav__cta ws-nav__cta--desktop" href="${ctaHrefEsc}">${escapeHtml(cta)}</a>`
    : '';
  const navMainLabel = escapeHtml(pickChrome('navMain', locale));
  const navMenuLabel = escapeHtml(pickChrome('navMenu', locale));
  const navCloseLabel = escapeHtml(pickChrome('navClose', locale));
  const drawerLogoHtml = logoImage
    ? `<img class="ws-nav__drawer-logo" src="${escapeHtml(logoImage)}" alt="" width="40" height="40" loading="eager" decoding="async" />`
    : '';
  const drawerTitleHtml = brandTitle
    ? `<span class="ws-nav__drawer-title">${escapeHtml(brandTitle)}</span>`
    : '';
  const drawerBrandInner = `${drawerLogoHtml}${drawerTitleHtml}`;
  const drawerSocialBesideLocale = Boolean(localeSwitcherDrawerHtml);
  const drawerSocialHtml = renderSiteSocialLinksHtml(block.content, locale, {
    surface: 'nav',
    showHeading: navArrangement === 'signal' && !drawerSocialBesideLocale,
    wrapClass: 'ws-nav__social-wrap--drawer',
  });
  const drawerToolsHtml =
    drawerSocialBesideLocale && drawerSocialHtml
      ? `<div class="ws-nav__drawer-tools">${localeSwitcherDrawerHtml}${drawerSocialHtml}</div>`
      : localeSwitcherDrawerHtml;
  const drawerEndSocialHtml =
    drawerSocialBesideLocale && drawerSocialHtml ? '' : drawerSocialHtml;
  const drawerHtml = hasLinks
    ? `<div class="ws-nav__bar-actions">${localeSwitcherBarHtml}${barCtaHtml}<input type="checkbox" id="${toggleId}" class="ws-nav__toggle-input" aria-hidden="true" tabindex="-1" /><label for="${toggleId}" class="ws-nav__toggle" aria-label="${navMenuLabel}"><span class="ws-nav__toggle-icon" aria-hidden="true"></span></label></div>
    <div class="ws-nav__backdrop" aria-hidden="true"></div>
    <div class="ws-nav__drawer">
      <div class="ws-nav__drawer-header">
        <a class="ws-nav__drawer-brand" href="${brandHref}">${drawerBrandInner}</a>
        <label for="${toggleId}" class="ws-nav__drawer-close" aria-label="${navCloseLabel}"><span class="ws-nav__drawer-close-icon" aria-hidden="true"></span></label>
      </div>
      <nav aria-label="${navMainLabel}"><ul class="ws-nav__links">${links}</ul></nav>
      ${drawerToolsHtml}
      <div class="ws-nav__drawer-end">${drawerEndSocialHtml}${drawerCtaHtml}</div>
      ${desktopCtaHtml}
    </div>`
    : `${localeSwitcherBarHtml}${localeSwitcherDrawerHtml}${
        ctaHtml
          ? `<a class="ws-nav__cta ws-nav__cta--inline" href="${ctaHrefEsc}">${escapeHtml(cta)}</a>`
          : ''
      }`;

  return `<header class="ws-nav ${arrangementClass}" data-block-id="${escapeHtml(block.blockId)}" data-component="Navigation_Header" data-nav-presentation="${escapeHtml(navPresentation)}" data-nav-arrangement="${escapeHtml(navArrangement)}">
  <div class="ws-nav__inner">
    <a class="ws-nav__brand" href="${brandHref}">${brandInner}</a>
    ${drawerHtml}
  </div>
  ${NAV_DRAWER_HASH_SCRIPT}
</header>`;
}

function renderHero(
  block: LayoutBlock,
  locale: string,
  defaultLocale: string,
  headingLevel: 'h1' | 'h2',
  pages: PageMeta[] | undefined,
  siteMode: SiteMode,
  allBlocks?: LayoutBlock[],
  publicPathPrefix?: string | null,
): string {
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaSecondary = pickBlockField(block.content, 'ctaSecondary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
    publicPathPrefix,
  });
  const ctaSecondaryHref = resolveBlockCtaHref('browse', {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaSecondaryHref',
    siteMode,
    pages,
    allBlocks,
    publicPathPrefix,
  });
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1400, 78) : '';
  const heroImageSrcSet = heroImage ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600], 78) : '';
  const imageAlt = heading || eyebrow || 'Hero';
  const visualHtml = heroImage
    ? `<img src="${escapeHtml(heroImageSrc)}" srcset="${escapeHtml(heroImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroSplit)}" alt="${escapeHtml(imageAlt)}" width="1200" height="900" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero__visual-placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero" data-block-id="${escapeHtml(block.blockId)}" data-component="Hero_Editorial_Split" id="top">
  <div id="about" class="ws-hero__copy">
    <div class="ws-hero__copy-inner">
      ${eyebrow ? `<span class="ws-hero__eyebrow">${escapeHtml(eyebrow)}</span>` : ''}
      ${heading ? `<${headingLevel} class="ws-hero__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
      ${subheading ? `<span class="ws-hero__lead">${escapeHtml(subheading)}</span>` : ''}
      <div class="ws-hero__actions">
        ${ctaPrimary ? `<a class="ws-hero__btn-primary" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
        ${ctaSecondary ? `<a class="ws-hero__btn-secondary" href="${escapeHtml(ctaSecondaryHref)}">${escapeHtml(ctaSecondary)}</a>` : ''}
      </div>
    </div>
  </div>
  <div class="ws-hero__visual" aria-hidden="${heroImage ? 'false' : 'true'}">${visualHtml}</div>
</section>`;
}

function renderMenuItemsList(
  items: MenuItem[],
  locale: string,
  options: { wrapInListItem?: boolean; dietaryInline?: boolean } = {},
): string {
  const dietaryInline = options.dietaryInline === true;
  return items
    .map((item) => {
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, {
        iconsOnly: dietaryInline,
        placement: dietaryInline ? 'inline' : 'stack',
      });
      const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
      const desc = item.description
        ? `<p class="ws-menu__item-desc">${escapeHtml(item.description)}</p>`
        : '';
      const copyHtml = dietaryInline
        ? `<div class="ws-menu__item-primary">${name}${dietary}</div>${desc}`
        : `${name}${desc}${dietary}`;
      const article = `<article class="ws-menu__item">${renderMenuItemCodedRowHtml({
        code: item.code,
        price: item.price,
        copyHtml,
      })}</article>`;
      return options.wrapInListItem ? `<li>${article}</li>` : article;
    })
    .join('');
}

function renderMenu(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const menuImage = block.media.menuImage;
  const menuImageSrc = menuImage ? buildSiteImageProxyUrl(menuImage, 1600, 78) : '';
  const menuImageSrcSet = menuImage
    ? buildSiteImageSrcSet(menuImage, [480, 768, 1200, 1600, 2000], 78)
    : '';
  const columns = parseMenuColumns(menuDataRaw);
  const menuColumns = resolveMenuGridColumns(block.content);
  const introHtml = `<div class="ws-menu__intro">
    ${sectionTitle ? `<h2 class="ws-menu__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${sectionSubtitle ? `<p class="ws-menu__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
  </div>`;
  const mastheadHtml = menuImage
    ? `<div class="ws-menu__masthead">
  <div class="ws-menu__visual">
    <img class="ws-menu__visual-frame" src="${escapeHtml(menuImageSrc)}" srcset="${escapeHtml(menuImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuBanner)}" alt="${escapeHtml(sectionTitle || 'Menu')}" width="1600" height="900" loading="eager" fetchpriority="high" decoding="async" />
    <div class="ws-menu__masthead-veil" aria-hidden="true"></div>
  </div>
  ${introHtml}
</div>`
    : introHtml;
  const columnsHtml = columns
    .map((col) => {
      const items = renderMenuItemsList(col.items ?? [], locale, { dietaryInline: true });
      return `<div><h3 class="ws-menu__column-title">${escapeHtml(col.title)}</h3>${items}</div>`;
    })
    .join('');
  const mastheadMod = menuImage ? ' ws-menu--masthead' : '';

  return `<section class="ws-menu${mastheadMod}" data-block-id="${escapeHtml(block.blockId)}" data-component="MenuGrid_3Column" data-menu-columns="${escapeHtml(menuColumns)}" id="menu">
  ${mastheadHtml}
  <div class="ws-menu__body">
    <div class="ws-menu__grid">${columnsHtml}</div>
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderDynamicMenuFlow(
  block: LayoutBlock,
  locale: string,
  allBlocks?: LayoutBlock[],
): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const stripDrinks = Boolean(
    allBlocks?.some((entry) => entry.componentType === LAYOUT_COMPONENT_TYPE.drinksListStatic),
  );
  const columns = parseMenuColumns(menuDataRaw).filter(
    (column) => !stripDrinks || !isDrinkLikeMenuCategoryTitle(column.title),
  );
  const groupName = `menu-flow-${block.blockId}`;
  const chromeId = `${groupName}-chrome`;
  const categoriesLabel = escapeHtml(sectionTitle || pickChrome('menuCategories', locale));
  const chipsOverflow = columns.length > MENU_FLOW_CHIP_SCROLL_THRESHOLD;
  const ruleHtml =
    sectionTitle || sectionSubtitle ? '<div class="ws-menu-flow__rule" aria-hidden="true"></div>' : '';

  const panelShowCss = columns
    .map(
      (_, index) =>
        `#${chromeId}:has(#${groupName}-tab-${index}:checked) #${groupName}-panel-${index}{display:block}`,
    )
    .join('');

  const tabs = columns
    .map((col, index) => {
      const title = escapeHtml(col.title.trim() || `Category ${index + 1}`);
      return `<span class="ws-menu-flow__tab-wrap"><input type="radio" name="${escapeHtml(groupName)}" id="${escapeHtml(groupName)}-tab-${index}" class="ws-menu-flow__radio"${index === 0 ? ' checked' : ''} /><label class="ws-menu-flow__tab" id="${escapeHtml(groupName)}-label-${index}" for="${escapeHtml(groupName)}-tab-${index}">${title}</label></span>`;
    })
    .join('');

  const panels = columns
    .map((col, index) => {
      const items = (col.items ?? [])
        .map((item) => {
          if (!item.name.trim() && !item.price.trim() && !item.description.trim()) {
            return '';
          }
          const dietary = renderMenuItemDietaryTagsHtml(item, locale, {
            iconsOnly: true,
            placement: 'inline',
          });
          const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
          const desc = item.description
            ? `<p class="ws-menu__item-desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<li><article class="ws-menu__item">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `<div class="ws-menu__item-primary">${name}${dietary}</div>${desc}`,
          })}</article></li>`;
        })
        .join('');
      return `<div class="ws-menu-flow__panel" id="${escapeHtml(groupName)}-panel-${index}" role="region" aria-labelledby="${escapeHtml(groupName)}-label-${index}"><ul class="ws-menu-flow__list">${items}</ul></div>`;
    })
    .join('');

  const overflowAttr = chipsOverflow ? ' data-chips-overflow="true"' : '';

  return `<section class="ws-menu-flow" data-block-id="${escapeHtml(block.blockId)}" data-component="Dynamic_Menu_Flow" id="menu">
  ${panelShowCss ? `<style>${panelShowCss}</style>` : ''}
  <div class="ws-menu-flow__intro">
    ${sectionTitle ? `<h2 class="ws-menu-flow__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${sectionSubtitle ? `<span class="ws-menu-flow__subtitle">${escapeHtml(sectionSubtitle)}</span>` : ''}
    ${ruleHtml}
  </div>
  <div class="ws-menu-flow__body">
    <div class="ws-menu-flow__chrome" id="${escapeHtml(chromeId)}"><div class="ws-menu-flow__tabs" role="radiogroup" aria-label="${categoriesLabel}" data-chip-count="${columns.length}"${overflowAttr}><div class="ws-menu-flow__rail">${tabs}</div></div><div class="ws-menu-flow__panels">${panels}</div></div>
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function pageHasLeadForms(blocks: LayoutBlock[]): boolean {
  return blocks.some((block) => {
    const type = block.componentType;
    return (
      type === 'Reservation_Form_Embed' ||
      type === 'Allergy_Capture_Secure_Form' ||
      type === 'Pre_Order_Pickup_Form' ||
      type === 'Private_Party_Inquiry_Form' ||
      type === 'Catering_Inquiry_Form' ||
      type === 'Contact_Block'
    );
  });
}

function pageHasHighlightZine(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.registryBlockKey === 'Static_Menu_Highlight_Zine',
  );
}

function pageHasHighlightPromenade(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.registryBlockKey === 'Static_Menu_Highlight_Strip',
  );
}

function pageHasHighlightHeroPlate(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.registryBlockKey === 'Static_Menu_Highlight_Hero_Plate',
  );
}

function pageHasImageGallery(blocks: LayoutBlock[]): boolean {
  return blocks.some((block) => block.componentType === 'Image_Gallery');
}

function pageHasMenuFlow(blocks: LayoutBlock[]): boolean {
  /*
   * Category Chips shares `.ws-menu-flow` UI with Dynamic_Menu_Flow but keeps
   * componentType MenuGrid_3Column + registry Static_Menu_Category_Chips.
   * Without this gate the sticky-chip scroll script never loads on published
   * Speisekarte pages, so switching chips leaves the dish list mid-scroll.
   */
  return blocks.some(
    (block) =>
      block.componentType === 'Dynamic_Menu_Flow' ||
      block.registryBlockKey === REGISTRY_BLOCK.presentation.staticMenuCategoryChips,
  );
}

function pageHasBackToTop(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.componentType === 'BackToTop_Float' && block.hidden !== true,
  );
}

function pageHasContactChannels(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.componentType === 'ContactChannels_Float' && block.hidden !== true,
  );
}

function pageHasUtilityBar(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.componentType === 'Utility_Bar' && block.hidden !== true,
  );
}

function pageHasAnnouncementBar(blocks: LayoutBlock[]): boolean {
  return blocks.some(
    (block) => block.componentType === 'Announcement_Bar' && block.hidden !== true,
  );
}

function renderReservationFallbackFields(locale: string): string {
  return defaultReservationFields(locale)
    .map((field) => renderFormInput(field, 'ws-reservation__field', locale))
    .join('');
}

/** Mirrors AllergyCaptureSecureForm React markup (default fixture path). */
function renderAllergyFallbackFields(consent: string, locale: string): string {
  const fields = defaultAllergyFields(locale)
    .map((field) => renderFormInput(field, 'ws-allergy__field', locale))
    .join('');
  const consentLabel = escapeHtml(consent || pickChrome('consentFallback', locale));
  return `${fields}<label class="ws-allergy__consent"><input type="checkbox" name="consent" required /><span>${consentLabel}</span></label>`;
}

function renderOperationalFormFields(
  block: LayoutBlock,
  locale: string,
  fieldClass = 'ws-form__field',
): string {
  const fields = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  if (fields.length === 0) {
    return '';
  }

  const consentField = fields.find((field) => field.name === 'consent');
  const inputs = fields
    .filter((field) => field.name !== 'consent')
    .map((field) => renderFormInput(field, fieldClass, locale))
    .join('');

  const consentHtml = consentField
    ? `<label class="ws-form__consent"><input type="checkbox" name="consent" required /><span>${escapeHtml(consentField.label)}</span></label>`
    : '';

  return `${inputs}${consentHtml}`;
}

function renderReservationForm(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const consent = pickBlockField(block.content, 'consent', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const floorTables = parseFloorTables(pickBlockField(block.content, 'floorTables', locale));
  const tableSelect = renderFloorTableSelect(
    floorTables,
    block.dataBinding?.kind === 'floor_plan' ? block.dataBinding.showCapacity : true,
    escapeHtml,
    pickBlockField(block.content, 'tableLabel', locale) || pickChrome('fieldTable', locale),
  );
  const consentFromFields = operational.find((field) => field.name === 'consent');
  const fieldInputs =
    operational.length > 0
      ? operational
          .filter((field) => field.name !== 'consent')
          .map((field) => renderFormInput(field, 'ws-reservation__field', locale))
          .join('')
      : renderReservationFallbackFields(locale);
  const consentLabel = escapeHtml(
    consentFromFields?.label || consent || pickChrome('consentFallback', locale),
  );
  const consentHtml = `<label class="ws-form__consent"><input type="checkbox" name="consent" required /><span>${consentLabel}</span></label>`;
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackReservation', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formKey = resolveLeadFormKey(block, 'reservation');
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-reservation__form',
    formKey,
    locale,
    successChromeKey: 'reservationStatusSuccess',
  });
  const isEditorial = block.registryBlockKey === 'Reservation_Editorial_Form';
  const editorialClass = isEditorial ? ' ws-reservation--editorial' : '';
  const registryAttr = block.registryBlockKey
    ? ` data-registry-key="${escapeHtml(block.registryBlockKey)}"`
    : '';
  return `<section class="ws-reservation${editorialClass}" data-block-id="${escapeHtml(block.blockId)}" data-component="Reservation_Form_Embed"${registryAttr} id="reserve">
  <div class="ws-reservation__inner">
    ${heading ? `<h2 class="ws-reservation__title">${escapeHtml(heading)}</h2>` : ''}
    ${supporting ? `<span class="ws-reservation__subtitle">${escapeHtml(supporting)}</span>` : ''}
    ${formOpen}
      ${tableSelect}${fieldInputs}
      ${consentHtml}
      <button type="submit" class="ws-reservation__submit">${submitLabel}</button>
      <p class="ws-form__status" role="status">${statusIdle}</p>
    </form>
    ${note ? `<span class="ws-reservation__note">${escapeHtml(note)}</span>` : ''}
  </div>
</section>`;
}

function renderTastingTimeline(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const coursesRaw = pickBlockField(block.content, 'courses', locale);
  const courses = coursesRaw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const hasCourseMedia = courses.some((_, index) =>
    Boolean(block.media?.[`courseImage${index}`]?.trim()),
  );
  const coursesHtml = courses
    .map((course, index) => {
      const mediaUrl = block.media?.[`courseImage${index}`]?.trim() ?? '';
      const mediaSrc = mediaUrl ? buildSiteImageProxyUrl(mediaUrl, 800, 78) : '';
      const mediaSrcSet = mediaUrl
        ? buildSiteImageSrcSet(mediaUrl, [400, 640, 800, 1200], 78)
        : '';
      const { step, name, note } = splitTastingCourseLabel(course);
      const stepLabel = step || String(index + 1).padStart(2, '0');
      const mediaHtml = mediaSrc
        ? `<div class="ws-tasting__course-media"><img class="ws-tasting__course-img" src="${escapeHtml(mediaSrc)}" srcset="${escapeHtml(mediaSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuDishCard)}" alt="${escapeHtml(name)}" width="800" height="600" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async" /></div>`
        : '';
      const bodyHtml = hasCourseMedia
        ? `<div class="ws-tasting__course-body"><span class="ws-tasting__course-label">${escapeHtml(name)}</span>${note ? `<span class="ws-tasting__course-note">${escapeHtml(note)}</span>` : ''}</div>`
        : `<span class="ws-tasting__course-label">${escapeHtml(course)}</span>`;
      return `<li class="ws-tasting__course">${mediaHtml}<span class="ws-tasting__course-index">${escapeHtml(stepLabel)}</span>${bodyHtml}</li>`;
    })
    .join('');
  const pairingWine = pickBlockField(block.content, 'pairingWine', locale);
  const pairingZero = pickBlockField(block.content, 'pairingZero', locale);
  const pairingHtml =
    pairingWine || pairingZero
      ? `<div class="ws-tasting__pairings">${
          pairingWine ? `<span class="ws-tasting__pairing">${escapeHtml(pairingWine)}</span>` : ''
        }${pairingZero ? `<span class="ws-tasting__pairing">${escapeHtml(pairingZero)}</span>` : ''}</div>`
      : '';
  const mediaMod = hasCourseMedia ? ' ws-tasting--course-media' : '';
  return `<section class="ws-tasting${mediaMod}" data-block-id="${escapeHtml(block.blockId)}" data-component="Tasting_Menu_Course_Timeline" id="tasting">
  <div class="ws-tasting__inner">
    ${heading ? `<h2 class="ws-tasting__title">${escapeHtml(heading)}</h2>` : ''}
    ${supporting ? `<span class="ws-tasting__subtitle">${escapeHtml(supporting)}</span>` : ''}
    <ol class="ws-tasting__timeline">${coursesHtml}</ol>
    ${pairingHtml}
  </div>
</section>`;
}

function renderAllergyCapture(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const consent = pickBlockField(block.content, 'consent', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const formBody =
    operational.length > 0
      ? (() => {
          const consentFromFields = operational.find((field) => field.name === 'consent');
          const inputs = operational
            .filter((field) => field.name !== 'consent')
            .map((field) => renderFormInput(field, 'ws-allergy__field', locale))
            .join('');
          const consentLabel = escapeHtml(
            consentFromFields?.label || consent || pickChrome('consentFallback', locale),
          );
          return `${inputs}<label class="ws-allergy__consent"><input type="checkbox" name="consent" required /><span>${consentLabel}</span></label>`;
        })()
      : renderAllergyFallbackFields(consent, locale);
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackAllergy', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formKey = resolveLeadFormKey(block, 'allergy');
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-allergy__form',
    formKey,
    locale,
  });
  return `<section class="ws-allergy" data-block-id="${escapeHtml(block.blockId)}" data-component="Allergy_Capture_Secure_Form" id="allergies">
  <div class="ws-allergy__inner">
    ${heading ? `<h2 class="ws-allergy__title">${escapeHtml(heading)}</h2>` : ''}
    ${supporting ? `<span class="ws-allergy__subtitle">${escapeHtml(supporting)}</span>` : ''}
    ${formOpen}
      ${formBody}
      <button type="submit" class="ws-allergy__submit">${submitLabel}</button>
      <p class="ws-form__status" role="status">${statusIdle}</p>
    </form>
  </div>
</section>`;
}

function renderHeroVideo(
  block: LayoutBlock,
  locale: string,
  defaultLocale: string,
  headingLevel: 'h1' | 'h2',
  pages: PageMeta[] | undefined,
  siteMode: SiteMode,
  allBlocks?: LayoutBlock[],
  publicPathPrefix?: string | null,
): string {
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
    publicPathPrefix,
  });
  const videoUrl = block.media.heroVideo;
  const posterUrl = block.media.heroPoster;
  const posterSrc = posterUrl ? buildSiteImageProxyUrl(posterUrl, 1600, 78) : '';
  const posterSrcSet = posterUrl ? buildSiteImageSrcSet(posterUrl, [480, 768, 1200, 1600], 78) : '';
  const imageAlt = escapeHtml(heading || eyebrow || 'Atmosphere');
  const stillHtml = posterSrc
    ? `<img class="ws-hero-video__poster" src="${escapeHtml(posterSrc)}" srcset="${escapeHtml(posterSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroVideo)}" alt="${imageAlt}" width="1600" height="900" loading="eager" decoding="async" />`
    : '<div class="ws-hero-video__placeholder" aria-hidden="true"></div>';
  const videoHtml = videoUrl
    ? `<video class="ws-hero-video__video" src="${escapeHtml(videoUrl)}"${posterSrc ? ` poster="${escapeHtml(posterSrc)}"` : ''} autoplay muted loop playsinline preload="metadata"></video>`
    : '';
  return `<section class="ws-hero-video" data-block-id="${escapeHtml(block.blockId)}" data-component="Hero_Video_Atmosphere" id="top">
  <div class="ws-hero-video__media" aria-hidden="${videoUrl || posterSrc ? 'false' : 'true'}">${stillHtml}${videoHtml}</div>
  <div id="about" class="ws-hero-video__overlay">
    ${eyebrow ? `<span class="ws-hero-video__eyebrow">${escapeHtml(eyebrow)}</span>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-video__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<span class="ws-hero-video__subtitle">${escapeHtml(subheading)}</span>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-video__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div>
</section>`;
}

function renderChefStoryBody(body: string): string {
  const paragraphs = body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return '';
  return `<div class="ws-chef-story__body">${paragraphs
    .map((paragraph) => `<p class="ws-chef-story__paragraph">${escapeHtml(paragraph)}</p>`)
    .join('')}</div>`;
}

const CHEF_NOTE_COUNT = 4;

function renderChefStoryNotes(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const chefName = pickBlockField(block.content, 'chefName', locale);
  const chefTitle = pickBlockField(block.content, 'chefTitle', locale);
  const notes: string[] = [];
  for (let index = 0; index < CHEF_NOTE_COUNT; index += 1) {
    const label = pickBlockField(block.content, `noteLabel${index}`, locale).trim();
    const body = pickBlockField(block.content, `noteBody${index}`, locale).trim();
    if (!label && !body) continue;
    const image = block.media?.[`noteImage${index}`];
    const imageSrc = image ? buildSiteImageProxyUrl(image, 720, 78) : '';
    const imageSrcSet = image ? buildSiteImageSrcSet(image, [240, 480, 720], 78) : '';
    const figure = imageSrc
      ? `<figure class="ws-chef-story__note-figure"><img class="ws-chef-story__note-photo" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.chefNote)}" alt="${escapeHtml(label || sectionTitle || 'Kitchen note')}" width="480" height="480" loading="lazy" decoding="async" /></figure>`
      : '';
    notes.push(
      `<article class="ws-chef-story__note">${figure}<div class="ws-chef-story__note-copy">${label ? `<span class="ws-chef-story__note-label">${escapeHtml(label)}</span>` : ''}${body ? `<p class="ws-chef-story__note-body">${escapeHtml(body)}</p>` : ''}</div></article>`,
    );
  }
  const registryAttr = block.registryBlockKey
    ? ` data-registry-key="${escapeHtml(block.registryBlockKey)}"`
    : '';
  return `<section class="ws-chef-story ws-chef-story--notes" data-block-id="${escapeHtml(block.blockId)}" data-component="Chef_Story_Block"${registryAttr} id="story">
  <div class="ws-chef-story__inner">
    <header class="ws-chef-story__masthead">
      ${chefTitle ? `<span class="ws-chef-story__role">${escapeHtml(chefTitle)}</span>` : ''}
      ${sectionTitle ? `<h2 class="ws-chef-story__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${chefName ? `<span class="ws-chef-story__name">${escapeHtml(chefName)}</span>` : ''}
    </header>
    <div class="ws-chef-story__notes">${notes.join('')}</div>
  </div>
</section>`;
}

const KITCHEN_TEAM_MEMBER_COUNT = 4;

function renderKitchenTeam(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const isRow = block.registryBlockKey === REGISTRY_BLOCK.presentation.kitchenTeamRow;
  const isBand = block.registryBlockKey === REGISTRY_BLOCK.presentation.kitchenTeamBand;
  const modifier = isRow
    ? 'ws-kitchen-team--row'
    : isBand
      ? 'ws-kitchen-team--band'
      : 'ws-kitchen-team--lead';
  const usesLeadPhoto = !isRow;
  const lead = block.media?.leadImage;
  const leadSrc = lead ? buildSiteImageProxyUrl(lead, 960, 78) : '';
  const leadSrcSet = lead ? buildSiteImageSrcSet(lead, [320, 480, 720, 960], 78) : '';
  const leadHtml =
    usesLeadPhoto && leadSrc
      ? `<div class="ws-kitchen-team__lead-visual"><img class="ws-kitchen-team__lead-photo" src="${escapeHtml(leadSrc)}" srcset="${escapeHtml(leadSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.kitchenTeamLead)}" alt="${escapeHtml(sectionTitle || 'Kitchen team')}" width="720" height="900" loading="lazy" decoding="async" /></div>`
      : usesLeadPhoto
        ? '<div class="ws-kitchen-team__lead-visual"></div>'
        : '';

  const members: string[] = [];
  for (let index = 0; index < KITCHEN_TEAM_MEMBER_COUNT; index += 1) {
    const name = pickBlockField(block.content, `memberName${index}`, locale).trim();
    const role = pickBlockField(block.content, `memberRole${index}`, locale).trim();
    const bio = pickBlockField(block.content, `memberBio${index}`, locale).trim();
    if (!name && !role && !bio) continue;
    const image = block.media?.[`memberImage${index}`];
    const imageSrc = image ? buildSiteImageProxyUrl(image, 720, 78) : '';
    const imageSrcSet = image ? buildSiteImageSrcSet(image, [320, 480, 720], 78) : '';
    const photo =
      isRow && imageSrc
        ? `<div class="ws-kitchen-team__card-photo"><img class="ws-kitchen-team__member-photo" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.kitchenTeamMember)}" alt="${escapeHtml(name || sectionTitle || 'Team member')}" width="480" height="600" loading="lazy" decoding="async" /></div>`
        : '';
    const body = `${photo}${name ? `<span class="ws-kitchen-team__member-name">${escapeHtml(name)}</span>` : ''}${role ? `<span class="ws-kitchen-team__member-role">${escapeHtml(role)}</span>` : ''}${bio ? `<p class="ws-kitchen-team__member-bio">${escapeHtml(bio)}</p>` : ''}`;
    members.push(
      isRow
        ? `<li class="ws-kitchen-team__card">${body}</li>`
        : `<li class="ws-kitchen-team__person">${body}</li>`,
    );
  }

  const listClass = isRow ? 'ws-kitchen-team__row' : 'ws-kitchen-team__roster';
  const registryAttr = block.registryBlockKey
    ? ` data-registry-key="${escapeHtml(block.registryBlockKey)}"`
    : '';
  return `<section class="ws-kitchen-team ${modifier}" data-block-id="${escapeHtml(block.blockId)}" data-component="Kitchen_Team_Block"${registryAttr} id="team">
  <div class="ws-kitchen-team__inner">
    ${leadHtml}
    <div class="ws-kitchen-team__copy">
      ${sectionTitle ? `<h2 class="ws-kitchen-team__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${supporting ? `<p class="ws-kitchen-team__supporting">${escapeHtml(supporting)}</p>` : ''}
      <ul class="${listClass}">${members.join('')}</ul>
    </div>
  </div>
</section>`;
}

function renderChefStory(block: LayoutBlock, locale: string): string {
  if (block.registryBlockKey === REGISTRY_BLOCK.presentation.chefStoryNotes) {
    return renderChefStoryNotes(block, locale);
  }
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const chefName = pickBlockField(block.content, 'chefName', locale);
  const chefTitle = pickBlockField(block.content, 'chefTitle', locale);
  const body = pickBlockField(block.content, 'body', locale);
  const portrait = block.media.portraitImage;
  const portraitSrc = portrait ? buildSiteImageProxyUrl(portrait, 720, 78) : '';
  const portraitSrcSet = portrait ? buildSiteImageSrcSet(portrait, [480, 720, 960], 78) : '';
  const portraitHtml = portrait
    ? `<img class="ws-chef-story__portrait-frame" src="${escapeHtml(portraitSrc)}" srcset="${escapeHtml(portraitSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.chefPortrait)}" alt="${escapeHtml(chefName || sectionTitle || 'Chef portrait')}" width="480" height="600" loading="lazy" decoding="async" />`
    : `<div class="ws-chef-story__portrait-placeholder" aria-hidden="true">${chefName ? `<span class="ws-chef-story__portrait-fallback-label">${escapeHtml(chefName)}</span>` : ''}</div>`;

  const modifier =
    block.registryBlockKey === 'Chef_Story_Quote_Led'
      ? 'ws-chef-story--quote-led'
      : block.registryBlockKey === 'Chef_Story_Kitchen_Strip'
        ? 'ws-chef-story--kitchen-strip'
        : 'ws-chef-story--portrait-split';
  const registryAttr = block.registryBlockKey
    ? ` data-registry-key="${escapeHtml(block.registryBlockKey)}"`
    : '';

  return `<section class="ws-chef-story ${modifier}" data-block-id="${escapeHtml(block.blockId)}" data-component="Chef_Story_Block"${registryAttr} id="story">
  <div class="ws-chef-story__inner">
    <div class="ws-chef-story__grid">
      <div class="ws-chef-story__portrait">${portraitHtml}</div>
      <div class="ws-chef-story__copy">
        ${sectionTitle ? `<h2 class="ws-chef-story__title">${escapeHtml(sectionTitle)}</h2>` : ''}
        ${chefName ? `<span class="ws-chef-story__name">${escapeHtml(chefName)}</span>` : ''}
        ${chefTitle ? `<span class="ws-chef-story__role">${escapeHtml(chefTitle)}</span>` : ''}
        ${body ? renderChefStoryBody(body) : ''}
      </div>
    </div>
  </div>
</section>`;
}

function parseStripItems(raw: string): { name: string; price: string }[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/\s+[—–-]\s+/);
      if (parts.length >= 2) {
        return { name: parts[0]!.trim(), price: parts.slice(1).join(' — ').trim() };
      }
      return { name: line, price: '' };
    });
}

function renderHoursScheduleHtml(
  rows: HoursScheduleRow[],
  className = 'ws-hours-schedule',
): string {
  if (rows.length === 0) return '';
  const body = rows
    .map(
      (row) =>
        `<div class="ws-hours-schedule__row"><span class="ws-hours-schedule__day">${escapeHtml(row.day)}</span><span class="ws-hours-schedule__time">${escapeHtml(row.time)}</span></div>`,
    )
    .join('');
  return `<div class="${className}">${body}</div>`;
}

function renderSimpleOpeningHours(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_CLOCK,
  );
  const hours = pickBlockField(block.content, 'hours', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const clockIcon = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'md',
    className: 'ws-opening-hours__icon',
    name: sectionIcon.name,
  });
  const hoursHtml = renderHoursScheduleHtml(
    parseHoursSchedule(hours),
    'ws-hours-schedule ws-opening-hours__hours',
  );

  return `<section class="ws-opening-hours" data-block-id="${escapeHtml(block.blockId)}" data-component="Simple_Opening_Hours" id="hours">
  <div class="ws-opening-hours__inner">
    <div class="ws-opening-hours__header">
      ${clockIcon}
      ${sectionTitle ? `<h2 class="ws-opening-hours__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    </div>
    ${hoursHtml}
    ${note ? `<span class="ws-opening-hours__note">${escapeHtml(note)}</span>` : ''}
  </div>
</section>`;
}

function renderAddressMapBlock(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const address = pickBlockField(block.content, 'address', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapImage = block.media.mapImage;
  const mapImageSrc = mapImage ? buildSiteImageProxyUrl(mapImage, 1200, 78) : '';
  const mapImageSrcSet = mapImage ? buildSiteImageSrcSet(mapImage, [480, 768, 1200], 78) : '';
  const mapIcon = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'md',
    className: 'ws-address-map__icon',
    name: sectionIcon.name,
  });

  let visualHtml: string;
  if (mapEmbedUrl) {
    visualHtml = `<iframe class="ws-address-map__embed" src="${escapeHtml(mapEmbedUrl)}" title="${escapeHtml(sectionTitle || 'Map')}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe>`;
  } else if (mapImageSrc) {
    visualHtml = `<img class="ws-address-map__photo" src="${escapeHtml(mapImageSrc)}" srcset="${escapeHtml(mapImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuBanner)}" alt="${escapeHtml(address || sectionTitle || 'Map')}" width="1200" height="800" loading="lazy" decoding="async" />`;
  } else {
    visualHtml = '<div class="ws-address-map__placeholder" aria-hidden="true"></div>';
  }

  return `<section class="ws-address-map" data-block-id="${escapeHtml(block.blockId)}" data-component="Address_Map_Block" id="map">
  <div class="ws-address-map__inner">
    <div class="ws-address-map__copy">
      <div class="ws-address-map__header">
        ${mapIcon}
        ${sectionTitle ? `<h2 class="ws-address-map__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      </div>
      ${address ? `<span class="ws-address-map__address">${escapeHtml(address)}</span>` : ''}
      ${directions ? `<span class="ws-address-map__directions">${escapeHtml(directions)}</span>` : ''}
    </div>
    <div class="ws-address-map__visual">${visualHtml}</div>
  </div>
</section>`;
}

function renderDrinksListStatic(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const columns = parseMenuColumns(menuDataRaw);
  const columnsHtml = columns
    .map((col) => {
      const items = (col.items ?? [])
        .filter((item) => item.name.trim() || item.price.trim())
        .map((item) => {
          const dietary = renderMenuItemDietaryTagsHtml(item, locale);
          const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
          const desc = item.description
            ? `<p class="ws-menu__item-desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<article class="ws-menu__item">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `${name}${desc}${dietary}`,
          })}</article>`;
        })
        .join('');
      return `<div class="ws-drinks-list__column">${col.title.trim() ? `<h3 class="ws-drinks-list__column-title">${escapeHtml(col.title)}</h3>` : ''}${items}</div>`;
    })
    .join('');

  return `<section class="ws-drinks-list" data-block-id="${escapeHtml(block.blockId)}" data-component="Drinks_List_Static" id="drinks">
  <div class="ws-drinks-list__inner">
    ${sectionTitle ? `<h2 class="ws-drinks-list__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${sectionSubtitle ? `<span class="ws-drinks-list__subtitle">${escapeHtml(sectionSubtitle)}</span>` : ''}
    <div class="ws-drinks-list__grid">${columnsHtml}</div>
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderStaticLocationText(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const locationLabel = pickBlockField(block.content, 'locationLabel', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const mapIcon = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'md',
    className: 'ws-static-location__icon',
    name: sectionIcon.name,
  });

  return `<section class="ws-static-location" data-block-id="${escapeHtml(block.blockId)}" data-component="Static_Location_Text" id="location">
  <div class="ws-static-location__inner">
    <div class="ws-static-location__header">
      ${mapIcon}
      ${sectionTitle ? `<h2 class="ws-static-location__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    </div>
    ${locationLabel ? `<span class="ws-static-location__label">${escapeHtml(locationLabel)}</span>` : ''}
    ${address ? `<span class="ws-static-location__address">${escapeHtml(address)}</span>` : ''}
    ${hours ? `<span class="ws-static-location__hours">${escapeHtml(hours)}</span>` : ''}
    ${directions ? `<span class="ws-static-location__directions">${escapeHtml(directions)}</span>` : ''}
  </div>
</section>`;
}

function renderStaticLocationStop(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const locationLabel = pickBlockField(block.content, 'locationLabel', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const hoursRows = parseHoursSchedule(hours);
  const hoursHeading = pickChrome('footerHours', locale);
  const hoursHtml =
    hoursRows.length > 0
      ? `<div class="ws-stop-card__hours-block"><p class="ws-stop-card__hours-heading">${escapeHtml(hoursHeading)}</p><div class="ws-stop-card__hours ws-hours-schedule">${hoursRows
          .map(
            (row) =>
              `<div class="ws-hours-schedule__row"><span class="ws-hours-schedule__day">${escapeHtml(row.day)}</span><span class="ws-hours-schedule__time">${escapeHtml(row.time)}</span></div>`,
          )
          .join('')}</div></div>`
      : '';
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapImage = block.media.mapImage;
  const mapImageSrc = mapImage ? buildSiteImageProxyUrl(mapImage, 1200, 78) : '';
  const mapImageSrcSet = mapImage ? buildSiteImageSrcSet(mapImage, [480, 768, 1200], 78) : '';
  const mapIcon = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'md',
    className: 'ws-stop-card__icon',
    name: sectionIcon.name,
  });

  let mapHtml = '';
  if (mapEmbedUrl) {
    mapHtml = `<div class="ws-stop-card__map"><iframe class="ws-stop-card__embed" src="${escapeHtml(mapEmbedUrl)}" title="${escapeHtml(sectionTitle || 'Map')}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe></div>`;
  } else if (mapImageSrc) {
    mapHtml = `<div class="ws-stop-card__map"><img class="ws-stop-card__photo" src="${escapeHtml(mapImageSrc)}" srcset="${escapeHtml(mapImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuBanner)}" alt="${escapeHtml(address || sectionTitle || 'Map')}" width="1200" height="800" loading="lazy" decoding="async" /></div>`;
  }

  return `<section class="ws-stop-card" data-block-id="${escapeHtml(block.blockId)}" data-component="Static_Location_Text" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="location">
  <div class="ws-stop-card__inner">
    <div class="ws-stop-card__copy">
      <header class="ws-stop-card__header">
        <span class="ws-static-location__icon-hit ws-stop-card__icon-hit">${mapIcon}</span>
        <div class="ws-stop-card__heading">
          ${sectionTitle ? `<h2 class="ws-stop-card__title">${escapeHtml(sectionTitle)}</h2>` : ''}
          ${locationLabel ? `<span class="ws-stop-card__label">${escapeHtml(locationLabel)}</span>` : ''}
        </div>
      </header>
      ${address ? `<span class="ws-stop-card__address">${escapeHtml(address)}</span>` : ''}
      ${hoursHtml}
      ${directions ? `<span class="ws-stop-card__directions">${escapeHtml(directions)}</span>` : ''}
    </div>
    ${mapHtml}
  </div>
</section>`;
}

function renderSimpleMenuStrip(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const itemsRaw = pickBlockField(block.content, 'items', locale);
  const items = parseStripItems(itemsRaw);
  const listHtml = items
    .map(
      (item) =>
        `<li class="ws-menu-strip__item"><span class="ws-menu-strip__name">${escapeHtml(item.name)}</span>${item.price ? `<span class="ws-menu-strip__price">${escapeHtml(item.price)}</span>` : ''}</li>`,
    )
    .join('');

  return `<section class="ws-menu-strip" data-block-id="${escapeHtml(block.blockId)}" data-component="Simple_Menu_Strip" id="menu">
  <div class="ws-menu-strip__inner">
    ${sectionTitle ? `<h2 class="ws-menu-strip__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    <ul class="ws-menu-strip__list">${listHtml}</ul>
  </div>
</section>`;
}

function renderSimpleMenuBoard(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const itemsRaw = pickBlockField(block.content, 'items', locale);
  const items = parseStripItems(itemsRaw);
  const listHtml = items
    .map(
      (item) =>
        `<li class="ws-menu-board__item"><span class="ws-menu-board__name">${escapeHtml(item.name)}</span>${item.price ? `<span class="ws-menu-board__leader" aria-hidden="true"></span><span class="ws-menu-board__price">${escapeHtml(item.price)}</span>` : ''}</li>`,
    )
    .join('');

  return `<section class="ws-menu-board" data-block-id="${escapeHtml(block.blockId)}" data-component="Simple_Menu_Strip" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  <div class="ws-menu-board__inner">
    <header class="ws-menu-board__intro">
      ${supporting ? `<span class="ws-menu-board__eyebrow">${escapeHtml(supporting)}</span>` : ''}
      ${sectionTitle ? `<h2 class="ws-menu-board__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    </header>
    <ul class="ws-menu-board__list">${listHtml}</ul>
  </div>
</section>`;
}

function renderPreOrderFallbackFields(locale: string): string {
  const pickupLabel =
    locale === 'de' ? 'Abholung' : locale === 'vi' ? 'Nhận hàng' : 'Pickup time';
  const itemsLabel =
    locale === 'de' ? 'Bestellung' : locale === 'vi' ? 'Đơn hàng' : 'Order items';
  return [
    { name: 'name', type: 'text', required: true, label: pickChrome('fieldName', locale) },
    { name: 'email', type: 'email', required: true, label: pickChrome('fieldEmail', locale) },
    { name: 'phone', type: 'tel', required: false, label: pickChrome('fieldPhone', locale) },
    { name: 'pickup', type: 'text', required: true, label: pickupLabel },
    { name: 'items', type: 'textarea', required: true, label: itemsLabel },
  ]
    .map((field) => renderFormInput(field, 'ws-form__field', locale))
    .join('');
}

function renderInquiryFallbackFields(locale: string, opts?: { dateRequired?: boolean; guestsRequired?: boolean }): string {
  const fields =
    opts?.dateRequired || opts?.guestsRequired
      ? defaultCateringFields(locale)
      : defaultPrivatePartyFields(locale);
  return fields.map((field) => renderFormInput(field, 'ws-form__field', locale)).join('');
}

function renderLiveBakeryCounterList(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const items = parseLabeledLines(pickBlockField(block.content, 'items', locale));
  const listHtml = items
    .map((item) => {
      const soldOut = /ausverkauft|sold\s*out|hết/i.test(item.meta);
      const fresh = /frisch|fresh|còn|mới/i.test(item.meta);
      const status = soldOut ? 'sold-out' : fresh ? 'fresh' : 'limited';
      const itemClass = [
        'ws-bakery-counter__item',
        soldOut ? 'ws-bakery-counter__item--sold-out' : '',
      ]
        .filter(Boolean)
        .join(' ');
      return `<li class="${itemClass}" data-status="${status}"><span class="ws-bakery-counter__name">${escapeHtml(item.label)}</span>${item.meta ? `<span class="ws-bakery-counter__status">${escapeHtml(item.meta)}</span>` : ''}</li>`;
    })
    .join('');

  return `<section class="ws-bakery-counter" data-block-id="${escapeHtml(block.blockId)}" data-component="Live_Bakery_Counter_List" id="bakery">
  <div class="ws-bakery-counter__inner">
    ${sectionTitle ? `<h2 class="ws-bakery-counter__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${supporting ? `<span class="ws-bakery-counter__supporting">${escapeHtml(supporting)}</span>` : ''}
    <ul class="ws-bakery-counter__list">${listHtml}</ul>
  </div>
</section>`;
}

function renderSocialFeedGrid(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const handle = pickBlockField(block.content, 'handle', locale);
  const posts = parseLabeledLines(pickBlockField(block.content, 'posts', locale));
  const gridHtml = posts
    .map(
      (post) =>
        `<li class="ws-social-feed__card"><p class="ws-social-feed__caption">${escapeHtml(post.label)}</p>${post.meta ? `<p class="ws-social-feed__meta">${escapeHtml(post.meta)}</p>` : ''}</li>`,
    )
    .join('');

  return `<section class="ws-social-feed" data-block-id="${escapeHtml(block.blockId)}" data-component="Social_Feed_Grid" id="social">
  <div class="ws-social-feed__inner">
    ${sectionTitle ? `<h2 class="ws-social-feed__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${handle ? `<span class="ws-social-feed__handle">${escapeHtml(handle)}</span>` : ''}
    <ul class="ws-social-feed__grid">${gridHtml}</ul>
  </div>
</section>`;
}

function renderCoffeeSubscriptionMatrix(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const plans = parseLabeledLines(pickBlockField(block.content, 'plans', locale));
  const plansHtml = plans
    .map(
      (plan) =>
        `<li class="ws-coffee-sub__plan"><p class="ws-coffee-sub__plan-name">${escapeHtml(plan.label)}</p>${plan.meta ? `<p class="ws-coffee-sub__plan-meta">${escapeHtml(plan.meta)}</p>` : ''}</li>`,
    )
    .join('');

  return `<section class="ws-coffee-sub" data-block-id="${escapeHtml(block.blockId)}" data-component="Coffee_Subscription_Matrix" id="subscribe">
  <div class="ws-coffee-sub__inner">
    ${sectionTitle ? `<h2 class="ws-coffee-sub__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${supporting ? `<span class="ws-coffee-sub__supporting">${escapeHtml(supporting)}</span>` : ''}
    <ul class="ws-coffee-sub__grid">${plansHtml}</ul>
    ${ctaLabel ? `<a class="ws-coffee-sub__cta" href="#contact">${escapeHtml(ctaLabel)}</a>` : ''}
  </div>
</section>`;
}

function renderPreOrderPickupForm(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const operationalFields = renderOperationalFormFields(block, locale, 'ws-form__field');
  const fields = operationalFields || renderPreOrderFallbackFields(locale);
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackReservation', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-preorder__form',
    formKey: resolveLeadFormKey(block, 'pre_order'),
    locale,
  });

  return `<section class="ws-preorder" data-block-id="${escapeHtml(block.blockId)}" data-component="Pre_Order_Pickup_Form" id="preorder">
  <div class="ws-preorder__inner">
    ${heading ? `<h2 class="ws-preorder__title">${escapeHtml(heading)}</h2>` : ''}
    ${supporting ? `<span class="ws-preorder__supporting">${escapeHtml(supporting)}</span>` : ''}
    ${formOpen}
      ${fields}
      <button type="submit" class="ws-preorder__submit">${submitLabel}</button>
      <p class="ws-form__status" role="status">${statusIdle}</p>
    </form>
    ${note ? `<span class="ws-preorder__note">${escapeHtml(note)}</span>` : ''}
  </div>
</section>`;
}

function renderDynamicCocktailSpotlight(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const cocktailName = pickBlockField(block.content, 'cocktailName', locale);
  const description = pickBlockField(block.content, 'description', locale);
  const price = pickBlockField(block.content, 'price', locale);
  const badgeText = pickBlockField(block.content, 'badgeText', locale);
  const image = block.media.cocktailImage;
  const imageSrc = image ? buildSiteImageProxyUrl(image, 960, 78) : '';
  const imageSrcSet = image ? buildSiteImageSrcSet(image, [480, 720, 960], 78) : '';
  const imageAlt = escapeHtml(cocktailName || sectionTitle || 'Cocktail');
  const visualHtml = imageSrc
    ? `<img class="ws-cocktail__photo" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroPedestal)}" alt="${imageAlt}" width="720" height="720" loading="lazy" decoding="async" />`
    : '<div class="ws-cocktail__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-cocktail" data-block-id="${escapeHtml(block.blockId)}" data-component="Dynamic_Cocktail_Spotlight" id="cocktail">
  <div class="ws-cocktail__inner">
    <div class="ws-cocktail__visual">${visualHtml}</div>
    <div class="ws-cocktail__copy">
      ${badgeText ? `<span class="ws-cocktail__badge">${escapeHtml(badgeText)}</span>` : ''}
      ${sectionTitle ? `<h2 class="ws-cocktail__eyebrow">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${cocktailName ? `<span class="ws-cocktail__name">${escapeHtml(cocktailName)}</span>` : ''}
      ${description ? `<span class="ws-cocktail__description">${escapeHtml(description)}</span>` : ''}
      ${price ? `<span class="ws-cocktail__price">${escapeHtml(price)}</span>` : ''}
    </div>
  </div>
</section>`;
}

function renderPrivatePartyInquiryForm(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const operationalFields = renderOperationalFormFields(block, locale, 'ws-form__field');
  const fields = operationalFields || renderInquiryFallbackFields(locale);
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackReservation', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-party-inquiry__form',
    formKey: resolveLeadFormKey(block, 'party'),
    locale,
  });

  return `<section class="ws-party-inquiry" data-block-id="${escapeHtml(block.blockId)}" data-component="Private_Party_Inquiry_Form" id="party">
  <div class="ws-party-inquiry__inner">
    ${heading ? `<h2 class="ws-party-inquiry__title">${escapeHtml(heading)}</h2>` : ''}
    ${supporting ? `<span class="ws-party-inquiry__supporting">${escapeHtml(supporting)}</span>` : ''}
    ${formOpen}
      ${fields}
      <button type="submit" class="ws-party-inquiry__submit">${submitLabel}</button>
      <p class="ws-form__status" role="status">${statusIdle}</p>
    </form>
    ${note ? `<span class="ws-party-inquiry__note">${escapeHtml(note)}</span>` : ''}
  </div>
</section>`;
}

function renderPrivatePartyInquirySalon(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const operationalFields = renderOperationalFormFields(block, locale, 'ws-form__field');
  const fields = operationalFields || renderInquiryFallbackFields(locale);
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackReservation', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-party-salon__form',
    formKey: resolveLeadFormKey(block, 'private_party'),
    locale,
  });
  const image = typeof block.media.salonImage === 'string' ? block.media.salonImage.trim() : '';
  const imageSrc = image ? buildSiteImageProxyUrl(image, 960, 78) : '';
  const imageSrcSet = image ? buildSiteImageSrcSet(image, [480, 720, 960, 1280], 78) : '';
  const visualHtml = imageSrc
    ? `<img class="ws-party-salon__frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.partySalon)}" alt="${escapeHtml(heading || 'Private dining')}" width="720" height="900" loading="lazy" decoding="async" />`
    : '<div class="ws-party-salon__frame ws-party-salon__frame--empty" aria-hidden="true"></div>';

  return `<section class="ws-party-salon" data-block-id="${escapeHtml(block.blockId)}" data-component="Private_Party_Inquiry_Form" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="party">
  <div class="ws-party-salon__inner">
    <div class="ws-party-salon__stage">${visualHtml}</div>
    <div class="ws-party-salon__lead">
      ${heading ? `<h2 class="ws-party-salon__title">${escapeHtml(heading)}</h2>` : ''}
      ${supporting ? `<span class="ws-party-salon__supporting">${escapeHtml(supporting)}</span>` : ''}
    </div>
    <div class="ws-party-salon__card">
      ${formOpen}
        ${fields}
        <button type="submit" class="ws-party-salon__submit">${submitLabel}</button>
        <p class="ws-form__status" role="status">${statusIdle}</p>
      </form>
      ${note ? `<span class="ws-party-salon__note">${escapeHtml(note)}</span>` : ''}
    </div>
  </div>
</section>`;
}

function renderTastingMenuPairingCards(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const courses = pickBlockField(block.content, 'courses', locale)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const pairingWine = pickBlockField(block.content, 'pairingWine', locale);
  const pairingZero = pickBlockField(block.content, 'pairingZero', locale);
  const coursesHtml = courses
    .map(
      (course, index) =>
        `<li class="ws-tasting-pairing__course"><span class="ws-tasting-pairing__index">${String(index + 1).padStart(2, '0')}</span><span class="ws-tasting-pairing__label">${escapeHtml(course)}</span></li>`,
    )
    .join('');
  const pairingHtml =
    pairingWine || pairingZero
      ? `<div class="ws-tasting-pairing__seals">${
          pairingWine ? `<span class="ws-tasting-pairing__seal">${escapeHtml(pairingWine)}</span>` : ''
        }${pairingZero ? `<span class="ws-tasting-pairing__seal">${escapeHtml(pairingZero)}</span>` : ''}</div>`
      : '';

  return `<section class="ws-tasting-pairing" data-block-id="${escapeHtml(block.blockId)}" data-component="Tasting_Menu_Course_Timeline" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="tasting">
  <div class="ws-tasting-pairing__inner">
    <header class="ws-tasting-pairing__intro">
      ${heading ? `<h2 class="ws-tasting-pairing__title">${escapeHtml(heading)}</h2>` : ''}
      ${supporting ? `<span class="ws-tasting-pairing__subtitle">${escapeHtml(supporting)}</span>` : ''}
    </header>
    ${pairingHtml}
    <ol class="ws-tasting-pairing__courses">${coursesHtml}</ol>
  </div>
</section>`;
}

function renderLiveTapListSync(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const taps = parseLabeledLines(pickBlockField(block.content, 'taps', locale));
  const listHtml = taps
    .map(
      (tap, index) =>
        `<li class="ws-tap-list__item"><span class="ws-tap-list__index">${String(index + 1).padStart(2, '0')}</span><span class="ws-tap-list__name">${escapeHtml(tap.label)}</span>${tap.meta ? `<span class="ws-tap-list__meta">${escapeHtml(tap.meta)}</span>` : ''}</li>`,
    )
    .join('');

  return `<section class="ws-tap-list" data-block-id="${escapeHtml(block.blockId)}" data-component="Live_Tap_List_Sync" id="taps">
  <div class="ws-tap-list__inner">
    ${sectionTitle ? `<h2 class="ws-tap-list__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${supporting ? `<span class="ws-tap-list__supporting">${escapeHtml(supporting)}</span>` : ''}
    <ol class="ws-tap-list__list">${listHtml}</ol>
  </div>
</section>`;
}

function renderVipTableBookingStrip(
  block: LayoutBlock,
  locale: string,
  defaultLocale: string,
  publicPathPrefix?: string | null,
): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const capacityNote = pickBlockField(block.content, 'capacityNote', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const partyHref = buildHomepageAnchorHref('#party', locale, defaultLocale, publicPathPrefix);

  return `<section class="ws-vip-strip" data-block-id="${escapeHtml(block.blockId)}" data-component="VIP_Table_Booking_Strip" id="vip">
  <div class="ws-vip-strip__inner">
    <div class="ws-vip-strip__copy">
      ${heading ? `<h2 class="ws-vip-strip__title">${escapeHtml(heading)}</h2>` : ''}
      ${supporting ? `<span class="ws-vip-strip__supporting">${escapeHtml(supporting)}</span>` : ''}
      ${capacityNote ? `<span class="ws-vip-strip__capacity">${escapeHtml(capacityNote)}</span>` : ''}
    </div>
    ${ctaLabel ? `<a class="ws-vip-strip__cta" href="${escapeHtml(partyHref)}">${escapeHtml(ctaLabel)}</a>` : ''}
  </div>
</section>`;
}

function renderRoutePlaceHtml(
  place: string,
  href: string | undefined,
  className: string,
): string {
  if (!place) return '';
  if (href) {
    return `<a class="${className}" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(place)}</a>`;
  }
  return `<span class="${className}">${escapeHtml(place)}</span>`;
}

function renderWeeklyScheduleRouteStrip(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const rows = parseRouteStops(pickBlockField(block.content, 'schedule', locale));
  const listHtml = rows
    .map(
      (row) =>
        `<li class="ws-week-schedule__row"><span class="ws-week-schedule__day">${escapeHtml(row.day)}</span>${renderRoutePlaceHtml(row.place, row.href, 'ws-week-schedule__place')}</li>`,
    )
    .join('');

  return `<section class="ws-week-schedule" data-block-id="${escapeHtml(block.blockId)}" data-component="Weekly_Schedule_Route_Strip" id="schedule">
  <div class="ws-week-schedule__inner">
    ${sectionTitle ? `<h2 class="ws-week-schedule__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${supporting ? `<span class="ws-week-schedule__supporting">${escapeHtml(supporting)}</span>` : ''}
    <ul class="ws-week-schedule__list">${listHtml}</ul>
  </div>
</section>`;
}

function renderWeeklyScheduleRouteBoard(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const rows = parseRouteStops(pickBlockField(block.content, 'schedule', locale));
  const listHtml = rows
    .map(
      (row) =>
        `<li class="ws-route-board__tile"><span class="ws-route-board__day">${escapeHtml(row.day)}</span>${renderRoutePlaceHtml(row.place, row.href, 'ws-route-board__place')}</li>`,
    )
    .join('');

  return `<section class="ws-route-board" data-block-id="${escapeHtml(block.blockId)}" data-component="Weekly_Schedule_Route_Strip" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="schedule">
  <div class="ws-route-board__inner">
    <header class="ws-route-board__intro">
      ${sectionTitle ? `<h2 class="ws-route-board__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${supporting ? `<span class="ws-route-board__supporting">${escapeHtml(supporting)}</span>` : ''}
    </header>
    <ol class="ws-route-board__grid">${listHtml}</ol>
  </div>
</section>`;
}

function renderCateringInquiryForm(block: LayoutBlock, locale: string): string {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const operationalFields = renderOperationalFormFields(block, locale, 'ws-form__field');
  const fields =
    operationalFields ||
    renderInquiryFallbackFields(locale, { dateRequired: true, guestsRequired: true });
  const submitLabel = escapeHtml(ctaLabel || pickChrome('submitFallbackReservation', locale));
  const statusIdle = escapeHtml(pickChrome('formStatusIdle', locale));
  const formOpen = renderLeadFormOpenTag({
    className: 'ws-catering__form',
    formKey: resolveLeadFormKey(block, 'catering'),
    locale,
  });

  return `<section class="ws-catering" data-block-id="${escapeHtml(block.blockId)}" data-component="Catering_Inquiry_Form" id="catering">
  <div class="ws-catering__inner">
    ${heading ? `<h2 class="ws-catering__title">${escapeHtml(heading)}</h2>` : ''}
    ${supporting ? `<span class="ws-catering__supporting">${escapeHtml(supporting)}</span>` : ''}
    ${formOpen}
      ${fields}
      <button type="submit" class="ws-catering__submit">${submitLabel}</button>
      <p class="ws-form__status" role="status">${statusIdle}</p>
    </form>
    ${note ? `<span class="ws-catering__note">${escapeHtml(note)}</span>` : ''}
  </div>
</section>`;
}

function renderLiveGpsLocationEmbed(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const statusLabel = pickBlockField(block.content, 'statusLabel', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const updatedAt = pickBlockField(block.content, 'updatedAt', locale);
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapIcon = renderLucideIconHtml(sectionIcon.iconNode, {
    size: 'md',
    className: 'ws-live-gps__icon',
    name: sectionIcon.name,
  });
  const visualHtml = mapEmbedUrl
    ? `<iframe class="ws-live-gps__embed" src="${escapeHtml(mapEmbedUrl)}" title="${escapeHtml(sectionTitle || 'Live location')}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe>`
    : '<div class="ws-live-gps__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-live-gps" data-block-id="${escapeHtml(block.blockId)}" data-component="Live_GPS_Location_Embed" id="gps">
  <div class="ws-live-gps__inner">
    <div class="ws-live-gps__copy">
      <div class="ws-live-gps__header">
        ${mapIcon}
        ${sectionTitle ? `<h2 class="ws-live-gps__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      </div>
      ${statusLabel ? `<span class="ws-live-gps__status">${escapeHtml(statusLabel)}</span>` : ''}
      ${address ? `<span class="ws-live-gps__address">${escapeHtml(address)}</span>` : ''}
      ${updatedAt ? `<span class="ws-live-gps__updated">${escapeHtml(updatedAt)}</span>` : ''}
    </div>
    <div class="ws-live-gps__visual">${visualHtml}</div>
  </div>
</section>`;
}

function renderPopUpTicketPurchaseCard(block: LayoutBlock, locale: string): string {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const eventTitle = pickBlockField(block.content, 'eventTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const dateLabel = pickBlockField(block.content, 'dateLabel', locale);
  const price = pickBlockField(block.content, 'price', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);

  return `<section class="ws-popup-ticket" data-block-id="${escapeHtml(block.blockId)}" data-component="PopUp_Ticket_Purchase_Card" id="tickets">
  <div class="ws-popup-ticket__inner">
    ${sectionTitle ? `<h2 class="ws-popup-ticket__eyebrow">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${eventTitle ? `<span class="ws-popup-ticket__title">${escapeHtml(eventTitle)}</span>` : ''}
    ${dateLabel ? `<span class="ws-popup-ticket__date">${escapeHtml(dateLabel)}</span>` : ''}
    ${supporting ? `<span class="ws-popup-ticket__supporting">${escapeHtml(supporting)}</span>` : ''}
    <div class="ws-popup-ticket__actions">
      ${price ? `<span class="ws-popup-ticket__price">${escapeHtml(price)}</span>` : ''}
      ${ctaLabel ? `<a class="ws-popup-ticket__cta" href="#contact">${escapeHtml(ctaLabel)}</a>` : ''}
    </div>
  </div>
</section>`;
}

function renderFooter(
  block: LayoutBlock,
  locale: string,
  pages: PageMeta[] | undefined,
  defaultLocale: string,
  publicPathPrefix?: string | null,
  themeId?: string,
  colorsetId?: string,
): string {
  return renderFooterStandardHtml(block, {
    locale,
    headingLevel: 'h2',
    pages,
    defaultLocale,
    siteMode: SITE_MODE.standard,
    publicPathPrefix,
    themeId,
    colorsetId,
  });
}

/** Fixed cookie settings bar — essential notice; paired with site-cookie-settings.js. */
function renderCookieSettingsBarHtml(locale: string, privacyHref: string): string {
  return `<div class="ws-cookie-settings" role="region" aria-label="${escapeHtml(pickChrome('cookieSettingsRegion', locale))}" hidden>
  <p class="ws-cookie-settings__body">${escapeHtml(pickChrome('cookieSettingsBody', locale))} <a class="ws-cookie-settings__link" href="${escapeHtml(privacyHref)}">${escapeHtml(pickChrome('cookieSettingsPolicyLink', locale))}</a></p>
  <div class="ws-cookie-settings__actions">
    <button type="button" class="ws-cookie-settings__reject" data-ws-cookie-settings-reject>${escapeHtml(pickChrome('cookieSettingsReject', locale))}</button>
    <button type="button" class="ws-cookie-settings__accept" data-ws-cookie-settings-accept>${escapeHtml(pickChrome('cookieSettingsAccept', locale))}</button>
  </div>
</div>`;
}

function renderLegalDefinitionList(
  block: LayoutBlock,
  locale: string,
  fieldKeys: readonly string[],
  labelFor: (fieldKey: string, locale: string) => string,
): string {
  const rows = fieldKeys
    .map((fieldKey) => {
      const value = pickBlockField(block.content, fieldKey, locale);
      if (!value) {
        return '';
      }
      return `<div class="ws-legal__row"><dt class="ws-legal__term">${escapeHtml(labelFor(fieldKey, locale))}</dt><dd class="ws-legal__value">${escapeHtml(value)}</dd></div>`;
    })
    .filter(Boolean)
    .join('');

  if (!rows) return '';
  return `<dl class="ws-legal__list">${rows}</dl>`;
}

function renderLegalProse(bodyHtml: string): string {
  const safe = presentLegalBodyHtml(bodyHtml);
  if (!safe) return '';
  return `<div class="ws-legal__prose">${safe}</div>`;
}

function renderLegalImprint(
  block: LayoutBlock,
  locale: string,
  headingLevel: 'h1' | 'h2',
  pages?: PageMeta[],
): string {
  const prose = renderLegalProse(pickBlockField(block.content, LEGAL_BODY_HTML_FIELD_KEY, locale));
  const fieldKeys = visibleLegalDefinitionKeys('impressum', prose);
  const title = resolveLegalPageHeading('impressum', locale, pages);
  return `<section class="ws-legal" data-block-id="${escapeHtml(block.blockId)}" data-component="Legal_Imprint_Block">
  <div class="ws-legal__inner">
    <${headingLevel} class="ws-legal__title">${escapeHtml(title)}</${headingLevel}>
    ${renderLegalDefinitionList(block, locale, fieldKeys, (key, loc) =>
      pickImprintFieldLabel(
        key as (typeof LEGAL_IMPRINT_FIELD_KEYS)[number],
        loc,
        pickBlockField(block.content, legalFieldLabelContentKey(key), loc),
      ),
    )}
    ${prose}
  </div>
</section>`;
}

function renderLegalPrivacy(
  block: LayoutBlock,
  locale: string,
  headingLevel: 'h1' | 'h2',
  pages?: PageMeta[],
): string {
  const prose = renderLegalProse(pickBlockField(block.content, LEGAL_BODY_HTML_FIELD_KEY, locale));
  const fieldKeys = visibleLegalDefinitionKeys('datenschutz', prose);
  const title = resolveLegalPageHeading('datenschutz', locale, pages);
  return `<section class="ws-legal" data-block-id="${escapeHtml(block.blockId)}" data-component="Legal_Privacy_Block">
  <div class="ws-legal__inner">
    <${headingLevel} class="ws-legal__title">${escapeHtml(title)}</${headingLevel}>
    ${renderLegalDefinitionList(block, locale, fieldKeys, (key, loc) =>
      pickPrivacyFieldLabel(
        key as (typeof LEGAL_PRIVACY_FIELD_KEYS)[number],
        loc,
        pickBlockField(block.content, legalFieldLabelContentKey(key), loc),
      ),
    )}
    ${prose}
  </div>
</section>`;
}

function renderBlockHtml(
  block: LayoutBlock,
  locale: string,
  primaryHeroId: string | null,
  primaryHeroRegistryKey: string | null,
  pages: PageMeta[] | undefined,
  defaultLocale: string,
  siteMode: SiteMode,
  allBlocks: LayoutBlock[],
  vertical?: string,
  themeId?: string,
  colorsetId?: string,
  publicPathPrefix?: string | null,
  supportedLocales?: string[],
  pathname?: string | null,
): string {
  const headingLevel =
    block.blockId === primaryHeroId ||
    block.componentType === 'Legal_Imprint_Block' ||
    block.componentType === 'Legal_Privacy_Block'
      ? 'h1'
      : 'h2';

  const variantRenderer = resolveBlockHtmlRenderer(block.registryBlockKey);
  if (variantRenderer) {
    return variantRenderer(block, {
      locale,
      headingLevel,
      pages,
      defaultLocale,
      supportedLocales,
      siteMode,
      vertical,
      themeId,
      colorsetId,
      allBlocks,
      publicPathPrefix,
      pathname,
    });
  }

  switch (block.componentType) {
    case 'Navigation_Header':
      return renderNavigation(
        block,
        locale,
        pages,
        defaultLocale,
        siteMode,
        vertical,
        themeId,
        colorsetId,
        allBlocks,
        publicPathPrefix,
        supportedLocales,
        pathname,
      );
    case 'Hero_Editorial_Split':
      return renderHero(block, locale, defaultLocale, headingLevel, pages, siteMode, allBlocks, publicPathPrefix);
    case 'MenuGrid_3Column':
      return renderMenu(block, locale);
    case 'Dynamic_Menu_Flow':
      return renderDynamicMenuFlow(block, locale, allBlocks);
    case 'Reservation_Form_Embed':
      return renderReservationForm(block, locale);
    case 'Reservation_Third_Party_Book':
      return renderReservationThirdPartyBook(block, locale);
    case 'Reservation_Messenger_Book':
      return renderReservationMessengerBook(block, locale);
    case 'Reservation_Call_Book':
      return renderReservationCallBook(block, locale);
    case 'Chef_Story_Block':
      return renderChefStory(block, locale);
    case 'Kitchen_Team_Block':
      return renderKitchenTeam(block, locale);
    case 'Image_Gallery':
      return renderImageGallery(block, locale);
    case 'Tasting_Menu_Course_Timeline':
      return renderTastingTimeline(block, locale);
    case 'Allergy_Capture_Secure_Form':
      return renderAllergyCapture(block, locale);
    case 'Hero_Video_Atmosphere':
      return renderHeroVideo(block, locale, defaultLocale, headingLevel, pages, siteMode, allBlocks, publicPathPrefix);
    case 'Footer_Standard':
      return renderFooter(block, locale, pages, defaultLocale, publicPathPrefix, themeId, colorsetId);
    case 'Legal_Imprint_Block':
      return renderLegalImprint(block, locale, headingLevel, pages);
    case 'Legal_Privacy_Block':
      return renderLegalPrivacy(block, locale, headingLevel, pages);
    case 'Simple_Opening_Hours':
      return renderSimpleOpeningHours(block, locale);
    case 'Address_Map_Block':
      return renderAddressMapBlock(block, locale);
    case 'Drinks_List_Static':
      return renderDrinksListStatic(block, locale);
    case 'Static_Location_Text':
      return block.registryBlockKey === REGISTRY_BLOCK.presentation.staticLocationStop
        ? renderStaticLocationStop(block, locale)
        : renderStaticLocationText(block, locale);
    case 'Simple_Menu_Strip':
      return block.registryBlockKey === REGISTRY_BLOCK.presentation.simpleMenuBoard
        ? renderSimpleMenuBoard(block, locale)
        : renderSimpleMenuStrip(block, locale);
    case 'Live_Bakery_Counter_List':
      return renderLiveBakeryCounterList(block, locale);
    case 'Social_Feed_Grid':
      return renderSocialFeedGrid(block, locale);
    case 'Coffee_Subscription_Matrix':
      return renderCoffeeSubscriptionMatrix(block, locale);
    case 'Pre_Order_Pickup_Form':
      return renderPreOrderPickupForm(block, locale);
    case 'Dynamic_Cocktail_Spotlight':
      return renderDynamicCocktailSpotlight(block, locale);
    case 'Private_Party_Inquiry_Form':
      return renderPrivatePartyInquiryForm(block, locale);
    case 'Live_Tap_List_Sync':
      return renderLiveTapListSync(block, locale);
    case 'VIP_Table_Booking_Strip':
      return renderVipTableBookingStrip(block, locale, defaultLocale, publicPathPrefix);
    case 'Weekly_Schedule_Route_Strip':
      return block.registryBlockKey === REGISTRY_BLOCK.presentation.weeklyScheduleRouteBoard
        ? renderWeeklyScheduleRouteBoard(block, locale)
        : renderWeeklyScheduleRouteStrip(block, locale);
    case 'Catering_Inquiry_Form':
      return renderCateringInquiryForm(block, locale);
    case 'Live_GPS_Location_Embed':
      return renderLiveGpsLocationEmbed(block, locale);
    case 'PopUp_Ticket_Purchase_Card':
      return renderPopUpTicketPurchaseCard(block, locale);
    case 'Contact_Block':
      return renderContactVisitHtml(block, locale);
    case 'Faq_Block':
      return renderFaqBlockHtml(block, locale);
    case 'BackToTop_Float':
      return renderBackToTopFloatHtml(block, locale);
    case 'ContactChannels_Float':
      return renderContactChannelsFloatHtml(block, locale);
    case 'Utility_Bar':
      return renderUtilityBarHtml(block, locale);
    case 'Announcement_Bar':
      return renderAnnouncementBarHtml(block, locale);
    default:
      return '';
  }
}

/** Path A edge — semantic HTML aligned with React SitePage blocks. */
export function renderSiteHtml(options: RenderSiteHtmlOptions): string {
  const {
    blocks,
    locale,
    themeId,
    colorsetId,
    customColorSets,
    spacingEnum = SPACING_ENUM_KEYS.balanced,
    cornerEnum,
    typographyEnum,
    title = 'Web Studio',
    description = '',
    headExtras = '',
    bodyAttributes = '',
    pages,
    defaultLocale = locale,
    supportedLocales,
    siteMode = SITE_MODE.standard,
    vertical,
    publicPathPrefix,
    pathname,
  } = options;

  const primaryHeroId = resolvePrimaryHeroId(blocks);
  const primaryHeroRegistryKey = resolvePrimaryHeroRegistryKey(blocks);
  const bodyBlocks = [...blocks]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((block) =>
      renderBlockHtml(
        block,
        locale,
        primaryHeroId,
        primaryHeroRegistryKey,
        pages,
        defaultLocale,
        siteMode,
        blocks,
        vertical,
        themeId,
        colorsetId,
        publicPathPrefix,
        supportedLocales,
        pathname,
      ),
    )
    .join('\n');

  const colorsetAttr = colorsetId ? ` data-colorset="${escapeHtml(colorsetId)}"` : '';
  const compositionAttrs = compositionSiteDataAttributes(themeId);
  const compositionAttrStr = Object.entries(compositionAttrs)
    .map(([key, value]) => (value ? ` ${key}="${escapeHtml(value)}"` : ''))
    .join('');
  const navGroundAttr = ` data-nav-ground="${escapeHtml(resolveNavGround(blocks))}"`;
  const themeInlineStyle = buildProjectThemeInlineStyle(themeId, colorsetId, spacingEnum, {
    cornerEnum,
    typographyEnum,
    customColorSets,
  });
  const siteStyleAttr = themeInlineStyle ? ` style="${escapeHtml(themeInlineStyle)}"` : '';
  const htmlThemeAttr = ` data-theme="${escapeHtml(themeId)}"${colorsetAttr}`;
  const leadFormsScript = pageHasLeadForms(blocks)
    ? `\n  <script src="/api/site-assets/site-lead-forms.js" defer></script>`
    : '';
  const highlightZineScript = pageHasHighlightZine(blocks)
    ? `\n  <script src="/api/site-assets/site-highlight-zine.js" defer></script>`
    : '';
  const highlightPromenadeScript = pageHasHighlightPromenade(blocks)
    ? `\n  <script src="/api/site-assets/site-highlight-promenade.js" defer></script>`
    : '';
  const highlightHeroPlateScript = pageHasHighlightHeroPlate(blocks)
    ? `\n  <script src="/api/site-assets/site-highlight-hero-plate.js" defer></script>`
    : '';
  const imageGalleryScript = pageHasImageGallery(blocks)
    ? `\n  <script src="/api/site-assets/site-image-gallery.js" defer></script>`
    : '';
  const menuFlowScript = pageHasMenuFlow(blocks)
    ? `\n  <script src="/api/site-assets/site-menu-flow.js" defer></script>`
    : '';
  const cookieSettingsScript = `\n  <script src="/api/site-assets/site-cookie-settings.js" defer></script>`;
  const backToTopScript = pageHasBackToTop(blocks)
    ? `\n  <script src="/api/site-assets/site-back-to-top.js" defer></script>`
    : '';
  const contactChannelsScript = pageHasContactChannels(blocks)
    ? `\n  <script src="/api/site-assets/site-contact-channels.js" defer></script>`
    : '';
  const utilityBarScript = pageHasUtilityBar(blocks)
    ? `\n  <script src="/api/site-assets/site-utility-bar.js" defer></script>`
    : '';
  const announcementBarScript = pageHasAnnouncementBar(blocks)
    ? `\n  <script src="/api/site-assets/site-announcement.js" defer></script>`
    : '';
  const privacyHref =
    resolveFooterLegalLinks(pages, locale, defaultLocale, publicPathPrefix).find(
      (link) => link.slug === 'datenschutz',
    )?.href ?? '/datenschutz';
  const cookieSettingsBar = renderCookieSettingsBarHtml(locale, privacyHref);

  return `<!DOCTYPE html>
<html lang="${escapeHtml(locale)}"${htmlThemeAttr}>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  ${description ? `<meta name="description" content="${escapeHtml(description)}" />` : ''}
  <link rel="stylesheet" href="/api/site-assets/themes.css" />
  <link rel="stylesheet" href="/api/site-assets/site.css" />
  ${headExtras}
</head>
<body${bodyAttributes ? ` ${bodyAttributes}` : ''}>
  <div class="ws-site" data-theme="${escapeHtml(themeId)}"${colorsetAttr}${navGroundAttr}${compositionAttrStr}${siteStyleAttr}>
    <main class="ws-page-canvas">
${bodyBlocks}
    </main>
    ${cookieSettingsBar}
  </div>${leadFormsScript}${highlightZineScript}${highlightPromenadeScript}${highlightHeroPlateScript}${imageGalleryScript}${menuFlowScript}${cookieSettingsScript}${backToTopScript}${contactChannelsScript}${utilityBarScript}${announcementBarScript}
</body>
</html>`;
}

export function extractPageMetaFromBlocks(
  blocks: LayoutBlock[],
  locale: string,
): { title: string; description: string } {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  const hero = ordered.find(
    (b) =>
      b.componentType === 'Hero_Editorial_Split' ||
      b.componentType === 'Hero_Video_Atmosphere' ||
      b.registryBlockKey?.startsWith('Hero_'),
  );
  const title = hero
    ? pickLocalized(hero.content.heading, locale)
    : pickLocalized(ordered[0]?.content.siteTitle, locale) || 'Web Studio';
  const description = hero
    ? pickLocalized(hero.content.subheading, locale)
    : '';
  return { title: title || 'Web Studio', description };
}
